import EditIcon from '@mui/icons-material/Edit';
import classNames from 'classnames';
import Hls from 'hls.js';
import React, { FC, useEffect, useRef, useState } from 'react';
import { circleApi } from 'src/api/circles';
import { CircleConstants } from 'src/api/constants/circle.constants';
import { usersApi } from 'src/api/user';
import { UploadIcon } from 'src/assets/icons/upload-icon';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { VideoCropModal } from 'src/components/common/VideoCropModal/VideoCropModal';
import { getVideoMetadata } from 'src/utils/videoUtils';
import styles from './VideoUpload.module.scss';

const mb = 1048576; // 1MB
const maxVideoSize = 104857600; // 100MB

interface VideoUploadProps {
  title?: string;
  description?: string;
  defaultVideo?: string;
  videoType?: 'video-welcome' | 'video-direct-message' | 'video-post';
  classNameVideoContainer?: string;
  maxFileSize?: number; // in bytes
  maxDuration?: number; // in seconds
  showInfoIcon?: boolean;
  videoHeight?: string;
  hideDescription?: boolean;
  error?: string;
  onUpdate: (fileId: string | File) => void;
}

export const VideoUpload: FC<VideoUploadProps> = ({
  title = 'Video',
  description,
  defaultVideo,
  classNameVideoContainer,
  videoType = 'video-welcome',
  maxFileSize = maxVideoSize,
  maxDuration = CircleConstants.maxChallengeLengthInSeconds,
  showInfoIcon = true,
  videoHeight = '350px',
  hideDescription = false,
  error: externalError,
  onUpdate,
}) => {
  const [previewVideo, setPreviewVideo] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isDefaultVideo, setIsDefaultVideo] = useState<boolean>(!!defaultVideo);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const requiresCrop =
    videoType === 'video-welcome' || videoType === 'video-post';

  useEffect(() => {
    if (defaultVideo) {
      setPreviewVideo(defaultVideo);
      setIsDefaultVideo(true);
    }
  }, [defaultVideo]);

  // HLS video support (dynamically switch based on isDefaultVideo and file type)
  useEffect(() => {
    if (isDefaultVideo && previewVideo && previewVideo.endsWith('.m3u8')) {
      const videoElement = document.getElementById(
        'video-player'
      ) as HTMLVideoElement;
      if (videoElement && Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(previewVideo);
        hls.attachMedia(videoElement);
      }
    }
  }, [previewVideo, isDefaultVideo]);

  const validateVideoDuration = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        if (video.duration > maxDuration) {
          setError(`Video duration should not exceed ${maxDuration} seconds.`);
          resolve(false);
        } else {
          resolve(true);
        }
      };
    });
  };

  const handleFileUpload = async (file: File) => {
    if (file.size > maxFileSize) {
      setError(
        `Video size should not exceed ${Math.round(maxFileSize / mb)}MB.`
      );
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const response = await usersApi.uploadFile({
        contentType: file.type,
        name: file.name,
        type: videoType,
      });
      const { uploadURL, key } = response;

      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      const previewUrl = URL.createObjectURL(file);
      setPreviewVideo(previewUrl);
      setIsDefaultVideo(false); // Uploaded video is not default, so no HLS

      const videoId = await circleApi.getVideoId(key);
      onUpdate(videoId);
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Error uploading file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const processFile = async (file: File) => {
    const isValid = await validateVideoDuration(file);
    if (!isValid) {
      return;
    }

    if (requiresCrop) {
      const { isVertical } = await getVideoMetadata(file);
      if (!isVertical) {
        setCropFile(file);
        return;
      }
    }

    await handleFileUpload(file);
  };

  const handleCropConfirm = async (croppedFile: File) => {
    setCropFile(null);
    await handleFileUpload(croppedFile);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) {
      await processFile(uploadedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleVideoClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    fileRef.current?.click();
  };

  const displayError = externalError || error;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        {/* {showInfoIcon && (
          <div className={styles.infoIcon}>
            <a
              href="/how-it-works#info-video"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.infoIcon}
            >
              <InfoOutlinedIcon style={{ color: 'red', cursor: 'pointer' }} />
            </a>
          </div>
        )} */}
      </header>

      {!hideDescription && description && (
        <p className={styles.description}>{description}</p>
      )}

      <div
        className={classNames(styles.videoContainer, classNameVideoContainer, {
          [styles.dragging]: isDragging,
          [styles.isVideoUploaded]: previewVideo && !isUploading,
        })}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        {...(!previewVideo && !isUploading
          ? { onClick: handleVideoClick }
          : {})}
      >
        {!previewVideo && !isUploading && (
          <div className={styles.videoWrapper}>
            <div>
              <div className={styles.icon}>
                <UploadIcon />
              </div>
              <div className={styles.uploadText}>Select video to upload</div>
              <div className={styles.dragText}>Or drag and drop</div>
            </div>
          </div>
        )}
        {isUploading && (
          <MaterialCircularProgress
            size={30}
            height="inherit"
            message="Uploading..."
          />
        )}
        {previewVideo && !isUploading && (
          <div className={styles.videoPreviewContainer}>
            {isDefaultVideo && previewVideo.endsWith('.m3u8') ? (
              <video
                id="video-player"
                controls
                className={styles.previewVideo}
                style={{ width: '100%', height: videoHeight, display: 'block' }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <video
                src={previewVideo}
                controls
                className={styles.previewVideo}
                style={{ width: '100%', height: videoHeight, display: 'block' }}
              >
                Your browser does not support the video tag.
              </video>
            )}
            <div
              className={styles.replaceButton}
              onClick={(e) => {
                e.stopPropagation();
                fileRef.current?.click();
              }}
            >
              <EditIcon className={styles.replaceIcon} />
            </div>
          </div>
        )}
      </div>

      {displayError && <div className={styles.errorText}>{displayError}</div>}

      {!displayError && (
        <div className={styles.maxDurationText}>
          Maximum {maxDuration} seconds
        </div>
      )}

      <input
        accept=".mp4, .mov, .avi"
        type="file"
        className={styles.hiddenInput}
        ref={fileRef}
        onChange={handleInputChange}
      />

      {cropFile && (
        <VideoCropModal
          open={true}
          videoFile={cropFile}
          onConfirm={handleCropConfirm}
          onCancel={() => {
            setCropFile(null);
            if (fileRef.current) {
              fileRef.current.value = '';
            }
          }}
        />
      )}
    </div>
  );
};
